import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Eye, EyeOff, Settings2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { fmtDate } from '../../utils/formatUtils.js';
import { clientEfFoutTekst } from '../../utils/clientAanmaakFouten.js';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { dutchDateToIso, maskDutchDate } from '../../utils/dateOfBirth.js';
import { primaryMembershipCredits, getCreditBalancesForMemberships } from '../../utils/membershipUtils.js';

function calcEndDate(membership) {
  if (!membership) return null;
  if (membership.end_date) return membership.end_date;
  if (membership.initial_end_date) return membership.initial_end_date;
  if (membership.start_date && membership.initial_duration_months) {
    const d = new Date(membership.start_date + 'T00:00:00');
    d.setDate(d.getDate() + membership.initial_duration_months * 31);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

export function NieuweClientModal({ onClose, onCreated, initialClientType = 'active' }) {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin_coach' || role === 'superadmin';
  const [voornaam, setVoornaam] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [coachId, setCoachId] = useState(user?.id || '');
  const [clientType, setClientType] = useState(initialClientType);
  const [membershipTypeId, setMembershipTypeId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [coaches, setCoaches] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [homeLocationId, setHomeLocationId] = useState('');
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [stuurActiveringsmail, setStuurActiveringsmail] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const [coachesRes, typesRes, locsRes] = await Promise.all([
          supabase.from('profiles').select('id, full_name').in('role', ['coach', 'admin_coach', 'superadmin']).is('deleted_at', null).order('full_name'),
          supabase.from('membership_types').select('id, name, is_active').eq('is_active', true).order('name'),
          supabase.from('locations').select('id, name').eq('is_active', true).order('name'),
        ]);
        if (coachesRes.error) throw coachesRes.error;
        if (typesRes.error) throw typesRes.error;
        setCoaches(coachesRes.data || []);
        setMembershipTypes(typesRes.data || []);
        setLocations(locsRes.data || []);
        if (!coachId && coachesRes.data?.some(coach => coach.id === user?.id)) setCoachId(user.id);
      } catch (err) {
        console.error('Error loading coaches/types:', err);
      }
    }
    loadData();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!voornaam.trim()) { setError(t('clients.validatieVoornaam')); return; }
    if (!achternaam.trim()) { setError(t('clients.validatieAchternaam')); return; }
    if (!email.trim()) { setError(t('clients.validatieEmail')); return; }
    if (!coachId) { setError(t('clients.validatieCoach')); return; }
    if (clientType === 'active' && !membershipTypeId) { setError(t('clients.validatieAbonnement')); return; }
    const dateOfBirthIso = dateOfBirth ? dutchDateToIso(dateOfBirth) : null;
    if (dateOfBirth && !dateOfBirthIso) { setError(t('clients.validatieGeboortedatum')); return; }

    setSaving(true);
    try {
      const fullName = `${voornaam.trim()} ${achternaam.trim()}`;

      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (!freshSession?.access_token) throw new Error(t('clients.geenSessie'));

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/create-client`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${freshSession.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          full_name: fullName,
          phone: phone.trim() || null,
          date_of_birth: dateOfBirthIso,
          coach_id: coachId,
          client_status: clientType,
          membership_type_id: clientType === 'active' ? membershipTypeId : null,
          membership_start_date: clientType === 'active' ? startDate : null,
          street: street.trim() || null,
          house_number: houseNumber.trim() || null,
          postal_code: postalCode.trim() || null,
          city: city.trim() || null,
          home_location_id: homeLocationId || null,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.error) {
        // Foutcodes van create-client vertalen naar een begrijpelijke melding
        // (i18n), inclusief de expliciete waarschuwing als de rollback van de
        // auth-gebruiker mislukte en het e-mailadres dus bezet blijft.
        throw new Error(clientEfFoutTekst(result));
      }

      const newClientId = result.user_id || result.client_id || result.id;

      if (stuurActiveringsmail && newClientId) {
        try {
          const { data: { session: sess2 } } = await supabase.auth.getSession();
          if (sess2?.access_token) {
            const mailResp = await fetch(`${supabase.supabaseUrl}/functions/v1/send-account-activation-email`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${sess2.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ client_id: newClientId }),
            });
            if (!mailResp.ok) console.warn('Activeringsmail kon niet worden verstuurd');
          }
        } catch (mailErr) {
          console.warn('Activeringsmail kon niet worden verstuurd:', mailErr);
        }
      }

      await onCreated({ id: newClientId, name: fullName, clientType });
      onClose({ id: newClientId, name: fullName, clientType });
    } catch (err) {
      setError(t('clients.foutAanmaken') + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initialClientType === 'prospect' ? t('prospects.createTitle') : t('clients.modalTitelNieuw')}</h2>
          <button className="modal-close" onClick={() => onClose()}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelVoornaam')} *</label>
              <input
                className="form-input"
                value={voornaam}
                onChange={e => setVoornaam(e.target.value)}
                placeholder={t('clients.placeholderVoornaam')}
                autoFocus
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelAchternaam')} *</label>
              <input
                className="form-input"
                value={achternaam}
                onChange={e => setAchternaam(e.target.value)}
                placeholder={t('clients.placeholderAchternaam')}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('clients.labelEmail')} *</label>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t('clients.placeholderEmail')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clients.labelPhone')}</label>
            <input
              className="form-input"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={t('clients.placeholderPhone')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clients.labelGeboortedatum')}</label>
            <input
              className="form-input"
              type="text"
              inputMode="numeric"
              autoComplete="bday"
              value={dateOfBirth}
              onChange={e => setDateOfBirth(maskDutchDate(e.target.value, dateOfBirth))}
              placeholder={t('clients.placeholderGeboortedatum')}
              maxLength={10}
            />
            <div style={{ marginTop: 5, fontSize: 12, color: 'var(--color-text-muted)' }}>
              {t('clients.hintGeboortedatum')}
            </div>
          </div>

          {isAdmin && (
            <div className="form-group">
              <label className="form-label">{t('clients.labelCoach')} *</label>
              <select
                className="form-input"
                value={coachId}
                onChange={e => setCoachId(e.target.value)}
              >
                <option value="">{t('clients.selecteerCoach')}</option>
                {coaches.length === 0 && <option disabled>{t('clients.geenCoachesGevonden')}</option>}
                {coaches.map(c => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('clients.labelType')} *</label>
            <div style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="clientType"
                  value="active"
                  checked={clientType === 'active'}
                  onChange={e => setClientType(e.target.value)}
                  style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                />
                {t('clients.typeActief')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="clientType"
                  value="prospect"
                  checked={clientType === 'prospect'}
                  onChange={e => setClientType(e.target.value)}
                  style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                />
                {t('clients.typeProspect')}
              </label>
            </div>
          </div>

          {clientType === 'active' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('clients.labelAbonnement')} *</label>
                <select
                  className="form-input"
                  value={membershipTypeId}
                  onChange={e => setMembershipTypeId(e.target.value)}
                >
                  <option value="">{t('clients.selecteerAbonnement')}</option>
                  {membershipTypes.length === 0 && <option disabled>{t('clients.geenAbonnementenGevonden')}</option>}
                  {membershipTypes.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('clients.labelStartdatum')}</label>
                <input
                  className="form-input"
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                />
              </div>
            </>
          )}

          <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 16 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-muted)', marginBottom: 12 }}>{t('clients.sectionAdres')}</p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelStraat')}</label>
                <input className="form-input" value={street} onChange={e => setStreet(e.target.value)} placeholder={t('clients.placeholderStraat')} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelHuisnummer')}</label>
                <input className="form-input" value={houseNumber} onChange={e => setHouseNumber(e.target.value)} placeholder={t('clients.placeholderHuisnummer')} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelPostcode')}</label>
                <input className="form-input" value={postalCode} onChange={e => setPostalCode(e.target.value)} placeholder={t('clients.placeholderPostcode')} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('clients.labelWoonplaats')}</label>
                <input className="form-input" value={city} onChange={e => setCity(e.target.value)} placeholder={t('clients.placeholderWoonplaats')} />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clients.labelStandaardLocatie')}</label>
              <select className="form-input" value={homeLocationId} onChange={e => setHomeLocationId(e.target.value)}>
                <option value="">{t('clients.geenLocatie')}</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ padding: '12px 0', borderTop: '1px solid var(--color-border)', marginTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={stuurActiveringsmail}
                onChange={e => setStuurActiveringsmail(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
              />
              <span style={{ fontSize: 14 }}>{t('accountActivation.checkboxLabel')}</span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose()} disabled={saving}>
              {t('clients.annuleren')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('auth.loading') : t('clients.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TrialPopupModal({ client, onClose, onKiesPT, onKiesGroepsles }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('clients.trialPopupTitel')}</h2>
        </div>
        <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 20 }}>
          {t('clients.trialPopupUitleg').replace('{naam}', client.name)}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className="btn btn-primary" onClick={onKiesPT} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupJaPT')}
          </button>
          <button className="btn btn-primary" onClick={onKiesGroepsles} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupJaGroepsles')}
          </button>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: '12px 16px' }}>
            {t('clients.trialPopupNee')}
          </button>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_CARD_LAYOUT = [
  { id: 'membership', visible: true },
  { id: 'credits', visible: true },
  { id: 'validUntil', visible: true },
  { id: 'coach', visible: true },
  { id: 'account', visible: true },
  { id: 'email', visible: false },
];

const CARD_FIELD_LABELS = {
  membership: 'clients.cardFieldMembership',
  credits: 'clients.cardFieldCredits',
  validUntil: 'clients.cardFieldValidUntil',
  coach: 'clients.cardFieldCoach',
  account: 'clients.cardFieldAccount',
  email: 'clients.cardFieldEmail',
};

function normaliseCardLayout(value) {
  if (!Array.isArray(value)) return DEFAULT_CARD_LAYOUT;
  const known = new Map(DEFAULT_CARD_LAYOUT.map(item => [item.id, item]));
  const result = value
    .filter(item => item && known.has(item.id))
    .map(item => ({ id: item.id, visible: item.visible !== false }));
  DEFAULT_CARD_LAYOUT.forEach(item => {
    if (!result.some(candidate => candidate.id === item.id)) result.push(item);
  });
  return result;
}

function CardLayoutModal({ layout, saving, onChange, onReset, onSave, onClose }) {
  function move(index, direction) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= layout.length) return;
    const next = [...layout];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  }

  return (
    <div className="modal-overlay">
      <div className="modal client-layout-modal">
        <div className="modal-header">
          <div>
            <h2 className="modal-title">{t('clients.layoutTitle')}</h2>
            <p className="text-muted client-layout-subtitle">{t('clients.layoutSubtitle')}</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label={t('common.sluiten')}>✕</button>
        </div>
        <div className="client-layout-scope">
          <strong>{t('clients.layoutMyLayout')}</strong>
          <span>{t('clients.layoutTenantHint')}</span>
        </div>
        <div className="client-layout-list">
          {layout.map((item, index) => (
            <div className="client-layout-row" key={item.id}>
              <span className="client-layout-order">{index + 1}</span>
              <span>{t(CARD_FIELD_LABELS[item.id])}</span>
              <div className="client-layout-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => move(index, -1)} disabled={index === 0} aria-label={t('clients.layoutMoveUp')}><ArrowUp size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => move(index, 1)} disabled={index === layout.length - 1} aria-label={t('clients.layoutMoveDown')}><ArrowDown size={14} /></button>
                <button
                  className={`btn btn-sm ${item.visible ? 'btn-secondary' : 'btn-ghost'}`}
                  onClick={() => onChange(layout.map(candidate => candidate.id === item.id ? { ...candidate, visible: !candidate.visible } : candidate))}
                  aria-pressed={item.visible}
                >
                  {item.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  {t(item.visible ? 'clients.layoutVisible' : 'clients.layoutHidden')}
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="modal-footer client-layout-footer">
          <button className="btn btn-ghost" onClick={onReset}>{t('clients.layoutReset')}</button>
          <div>
            <button className="btn btn-ghost" onClick={onClose}>{t('clients.annuleren')}</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving}>{saving ? t('common.bezig') : t('clients.layoutSave')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'prospect' | 'inactive'
  const [showModal, setShowModal] = useState(false);
  const [trialPopupClient, setTrialPopupClient] = useState(null);
  const [cardLayout, setCardLayout] = useState(DEFAULT_CARD_LAYOUT);
  const [draftCardLayout, setDraftCardLayout] = useState(DEFAULT_CARD_LAYOUT);
  const [preferences, setPreferences] = useState({});
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [savingLayout, setSavingLayout] = useState(false);
  const { showSuccess } = useToast();

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    async function loadPreferences() {
      if (!user?.id) return;
      const { data, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', user.id)
        .maybeSingle();
      if (preferencesError) return;
      const nextPreferences = data?.preferences || {};
      const nextLayout = normaliseCardLayout(nextPreferences.coach_clients_card_layout);
      setPreferences(nextPreferences);
      setCardLayout(nextLayout);
      setDraftCardLayout(nextLayout);
    }
    loadPreferences();
  }, [user?.id]);

  function getClientStatus(c) {
    // Prospect heeft voorrang
    if (c.client_status === 'prospect') return 'prospect';
    // Anders kijken naar lidmaatschap
    const today = new Date().toISOString().slice(0, 10);
    const isActive = (c.memberships || []).some(item => {
      const endDate = calcEndDate(item);
      return endDate === null || endDate >= today;
    });
    return isActive ? 'active' : 'inactive';
  }

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
    if (!matchesSearch) return false;
    if (statusFilter === 'all') return true;
    return getClientStatus(c) === statusFilter;
  });

  async function handleClientCreated(created) {
    showSuccess(t('clients.succesBericht'));
    if (created?.id) navigate(`/coach/clients/${created.id}`);
  }

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data: profiles, error: profilesErr } = await supabase
        .from('profiles')
        .select('id, full_name, email, customer_number, client_status, account_activated_at, deleted_at')
        .eq('role', 'client')
        .order('full_name', { ascending: true });

      if (profilesErr) throw profilesErr;

      if (!profiles || profiles.length === 0) {
        setClients([]);
        return;
      }

      const clientIds = profiles.map(p => p.id).filter(Boolean);
      const today = new Date().toISOString().slice(0, 10);

      const [membRes, ccRes, pipelineRes] = await Promise.all([
        clientIds.length > 0
          ? supabase
              .from('client_memberships')
              // Fase 3 (2026-08-09): de poolkolommen zijn hier weg. Het saldo
              // komt uit credit_batches via v_membership_credit_balance —
              // één extra query voor de hele lijst, geen N calls.
              .select('id, client_id, session_type, credits_per_week, start_date, end_date, initial_end_date, initial_duration_months, status, membership_types(name)')
              .in('client_id', clientIds)
              .eq('status', 'active')
              .order('start_date', { ascending: false })
          : Promise.resolve({ data: [] }),
        supabase
          .from('coach_clients')
          .select('client_id, coach_id, is_primary')
          .eq('is_primary', true)
          .in('client_id', clientIds),
        supabase
          .from('prospect_pipeline')
          .select('prospect_id, stage, completion_reason')
          .in('prospect_id', clientIds),
      ]);

      if (membRes.error) throw membRes.error;
      if (pipelineRes.error) throw pipelineRes.error;

      // Saldo uit credit_batches, één query voor alle abonnementen tegelijk.
      const balances = await getCreditBalancesForMemberships(
        (membRes.data || []).map(m => m.id)
      ).catch(() => ({}));

      const membMap = {};
      (membRes.data || []).forEach(m => {
        if (!membMap[m.client_id]) membMap[m.client_id] = [];
        membMap[m.client_id].push({ ...m, credit_balance: balances[m.id] || null });
      });

      // Haal coachnamen apart op om RLS-afhankelijkheid op nested join te vermijden
      const coachIds = [...new Set((ccRes.data || []).map(cc => cc.coach_id).filter(Boolean))];
      let coachNamesMap = {};
      if (coachIds.length > 0) {
        const { data: coachProfiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', coachIds);
        (coachProfiles || []).forEach(cp => { coachNamesMap[cp.id] = cp.full_name; });
      }

      const coachMap = {};
      (ccRes.data || []).forEach(cc => {
        if (coachNamesMap[cc.coach_id]) coachMap[cc.client_id] = coachNamesMap[cc.coach_id];
      });

      const pipelineMap = Object.fromEntries((pipelineRes.data || []).map(row => [row.prospect_id, row]));

      setClients(profiles.map(p => ({
        ...p,
        memberships: membMap[p.id] || [],
        coachName: coachMap[p.id] || null,
        prospectPipeline: pipelineMap[p.id] || null,
      })));
    } catch (err) {
      setError(t('clients.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveCardLayout() {
    if (!user?.id) return;
    setSavingLayout(true);
    const nextPreferences = { ...preferences, coach_clients_card_layout: draftCardLayout };
    const { error: saveError } = await supabase.from('user_preferences').upsert({
      user_id: user.id,
      preferences: nextPreferences,
      updated_at: new Date().toISOString(),
    });
    setSavingLayout(false);
    if (saveError) {
      setError(t('clients.layoutSaveError') + saveError.message);
      return;
    }
    setPreferences(nextPreferences);
    setCardLayout(draftCardLayout);
    setShowLayoutModal(false);
    showSuccess(t('clients.layoutSaved'));
  }

  function cardFieldValue(client, fieldId, clientStatus) {
    const memberships = client.memberships || [];
    if (fieldId === 'membership') return memberships.length > 0
      ? memberships.map(item => item.membership_types?.name || t('clients.onbekend')).join(', ')
      : t('clients.geen');
    if (fieldId === 'credits') return memberships.length > 0
      ? memberships.reduce((sum, item) => sum + primaryMembershipCredits(item), 0)
      : '—';
    if (fieldId === 'validUntil') return memberships.length > 0
      ? memberships.map(item => fmtDate(calcEndDate(item))).join(', ')
      : '—';
    if (fieldId === 'coach') return client.coachName || '—';
    if (fieldId === 'account') return t(client.account_activated_at ? 'accountActivation.statusActiveShort' : 'accountActivation.statusPendingShort');
    if (fieldId === 'email') return client.email || '—';
    return clientStatus;
  }

  return (
    <div>
      {showModal && (
        <NieuweClientModal
          onClose={() => {
            setShowModal(false);
          }}
          onCreated={handleClientCreated}
        />
      )}

      {trialPopupClient && (
        <TrialPopupModal
          client={trialPopupClient}
          onClose={() => setTrialPopupClient(null)}
          onKiesPT={() => {
            const client = trialPopupClient;
            setTrialPopupClient(null);
            navigate('/coach/les/nieuw', {
              state: {
                prefillPT: true,
                type: 'personal',
                clientId: client.id,
                coachId: user.id,
                clientName: client.name,
                prefillTrial: true,
              },
            });
          }}
          onKiesGroepsles={() => {
            const client = trialPopupClient;
            setTrialPopupClient(null);
            navigate('/coach/rooster', {
              state: {
                filterType: 'group',
                trialClientId: client.id,
                trialClientName: client.name,
              },
            });
          }}
        />
      )}

      {showLayoutModal && (
        <CardLayoutModal
          layout={draftCardLayout}
          saving={savingLayout}
          onChange={setDraftCardLayout}
          onReset={() => setDraftCardLayout(DEFAULT_CARD_LAYOUT)}
          onSave={saveCardLayout}
          onClose={() => setShowLayoutModal(false)}
        />
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">{t('clients.title')}</h1>
          <p className="page-subtitle">{filtered.length} {t('clients.aantalGevonden')}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            {t('clients.toevoegenKnop')}
          </button>
          <input
            type="search"
            className="form-input"
            placeholder={t('clients.zoekPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 200px', minWidth: 0, maxWidth: 260 }}
          />
          <button className="btn btn-secondary" onClick={() => { setDraftCardLayout(cardLayout); setShowLayoutModal(true); }}>
            <Settings2 size={16} />
            {t('clients.layoutButton')}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {['all', 'active', 'prospect', 'inactive'].map(f => (
          <button
            key={f}
            className={statusFilter === f ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setStatusFilter(f)}
            style={{ fontSize: 13, padding: '6px 14px' }}
          >
            {t(`clients.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        filtered.length === 0 ? (
          <div className="card empty-state">
            <div style={{ fontSize: 32 }}>👥</div>
            <p>{search ? t('clients.geenResultaatZoek') : t('clients.geenClinten')}</p>
          </div>
        ) : (
          <div className="clients-card-grid">
            {filtered.map(c => {
              const clientStatus = getClientStatus(c);
              const initials = (c.full_name || '?').split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
              return (
                <article className="client-overview-card" key={c.id}>
                  <div className="client-overview-head">
                    <span className="client-overview-avatar">{initials}</span>
                    <div className="client-overview-person">
                      <strong>{c.full_name || '—'}</strong>
                      <small>{c.customer_number || t('clients.geenKlantnummer')}</small>
                    </div>
                    <span className={`client-status-badge is-${c.deleted_at ? 'deleted' : clientStatus}`}>
                      {c.deleted_at ? t('clients.statusVerwijderd') : t(`clients.status${clientStatus.charAt(0).toUpperCase()}${clientStatus.slice(1)}`)}
                    </span>
                  </div>
                  {clientStatus === 'prospect' && (
                    <div className="client-prospect-stage">
                      <span>{t('clients.cardProspectStage')}</span>
                      <strong>{t(`prospects.stages.${c.prospectPipeline?.stage || 'nieuwe_lead'}`)}</strong>
                    </div>
                  )}
                  <div className="client-overview-facts">
                    {cardLayout.filter(item => item.visible).map(item => (
                      <div className="client-overview-fact" key={item.id}>
                        <small>{t(CARD_FIELD_LABELS[item.id])}</small>
                        <span>{cardFieldValue(c, item.id, clientStatus)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="client-overview-footer">
                    <span>{c.account_activated_at ? t('accountActivation.statusActive') : t('accountActivation.statusPending')}</span>
                    <Link to={`/coach/clients/${c.id}`} className="client-overview-link">{t('clients.openClient')}</Link>
                  </div>
                </article>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
